import React, { useState, useEffect } from 'react';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, onSnapshot, query, orderBy, updateDoc, doc } from 'firebase/firestore';
import { HelpTicket } from '../types';
import { Bug, CheckCircle2, ListTodo, Image as ImageIcon, ExternalLink, Filter, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export default function HelpDeskAdmin() {
  const [tickets, setTickets] = useState<HelpTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'change_log' | 'addressed' | 'cleared'>('all');

  useEffect(() => {
    const q = query(collection(db, 'helpTickets'), orderBy('reportedAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTickets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as HelpTicket)));
      setIsLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'helpTickets');
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (ticketId: string, newStatus: 'open' | 'change_log' | 'addressed' | 'cleared') => {
    const updates: Partial<HelpTicket> = { status: newStatus };

    if (newStatus === 'cleared') {
      if (!window.confirm('Are you sure you want to dismiss this ticket?')) return;
    }

    if (newStatus === 'addressed') {
      const fixedVer = window.prompt('What version was this fixed in? (e.g. v1.3.0)', '');
      if (fixedVer !== null) {
        updates.fixedInVersion = fixedVer;
      } else {
        return; // Cancelled
      }
    }

    try {
      await updateDoc(doc(db, 'helpTickets', ticketId), updates);
      toast.success(`Ticket status updated to ${newStatus}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'helpTickets');
    }
  };

  const filteredTickets = tickets.filter(t => filterStatus === 'all' || t.status === filterStatus);

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'open':
        return <span className="bg-lcars-red text-[var(--foreground)] px-2 py-0.5 rounded-full text-[0.6rem] font-bold uppercase animate-pulse">Open</span>;
      case 'change_log':
        return <span className="bg-lcars-gold text-black px-2 py-0.5 rounded-full text-[0.6rem] font-bold uppercase">Change Log</span>;
      case 'addressed':
        return <span className="bg-lcars-green text-black px-2 py-0.5 rounded-full text-[0.6rem] font-bold uppercase">Addressed</span>;
      case 'cleared':
        return <span className="bg-white/20 text-[var(--foreground)]/60 px-2 py-0.5 rounded-full text-[0.6rem] font-bold uppercase">Cleared</span>;
      default:
        return null;
    }
  };

  return (
    <div className="bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-red pl-3 lg:pl-6 py-2">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Bug className="w-8 h-8 text-lcars-red" />
          <div>
            <span className="text-xl font-bold text-lcars-red uppercase block leading-none">Engineering Tickets</span>
            <span className="text-[0.65rem] text-lcars-red font-bold uppercase tracking-widest block mt-1">Anomaly & Change Log System</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2 overflow-x-auto w-full lg:w-auto">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full p-1">
            <Filter className="w-4 h-4 text-[var(--foreground)]/40 ml-2" />
            <select 
              className="bg-transparent text-[var(--foreground)] text-base md:text-xs font-bold uppercase tracking-widest outline-none pr-4"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <option value="all">ALL TICKETS</option>
              <option value="open">OPEN ANOMALIES</option>
              <option value="change_log">CHANGE LOG</option>
              <option value="addressed">ADDRESSED</option>
              <option value="cleared">CLEARED</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-4 pr-2">
        {isLoading ? (
          <div className="text-lcars-red animate-pulse text-center py-8 font-bold uppercase tracking-widest">
            Scanning sub-space frequencies...
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-[var(--foreground)]/40 text-center py-8 font-bold uppercase tracking-widest">
            No engineering tickets found in this sector.
          </div>
        ) : (
          filteredTickets.map(ticket => (
            <div key={ticket.id} className={`bg-white/5 border border-white/10 rounded-xl p-4 lg:p-6 transition-all hover:border-lcars-red/30 ${ticket.status === 'open' ? 'border-lcars-red/50 bg-lcars-red/5' : ''}`}>
              <div className="flex flex-col lg:flex-row gap-6">
                
                {/* Left Col: Info */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-xl font-bold text-lcars-red">{ticket.ticketNumber}</span>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <div className="text-xs text-[var(--foreground)]/60 font-bold uppercase tracking-widest flex flex-wrap items-center gap-2">
                        <span>Reported by: <span className="text-[var(--foreground)]">{ticket.reportedByName}</span></span>
                        <span>|</span>
                        <span>{format(parseISO(ticket.reportedAt), 'MMM dd, yyyy HH:mm')}</span>
                        {ticket.appVersion && (
                          <>
                            <span>|</span>
                            <span className="text-lcars-blue">v{ticket.appVersion.replace('v', '')}</span>
                          </>
                        )}
                        {ticket.fixedInVersion && (
                          <>
                            <span>|</span>
                            <span className="text-lcars-green">Fixed In: {ticket.fixedInVersion}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-[var(--background)]/50 border-l-2 border-lcars-red/30 p-4 text-sm text-[var(--foreground)]/90 whitespace-pre-wrap rounded-r-lg font-mono">
                    {ticket.description}
                  </div>

                  <div className="flex gap-2 pt-2 flex-wrap">
                    <button 
                      onClick={() => handleStatusChange(ticket.id!, 'open')}
                      disabled={ticket.status === 'open'}
                      className="lcars-pill bg-lcars-red text-[var(--foreground)] text-[0.6rem] font-bold px-4 py-1.5 disabled:opacity-30 flex items-center gap-1 hover:bg-white hover:text-black transition-all"
                    >
                      <Bug className="w-3 h-3" /> MARK OPEN
                    </button>
                    <button 
                      onClick={() => handleStatusChange(ticket.id!, 'change_log')}
                      disabled={ticket.status === 'change_log'}
                      className="lcars-pill bg-lcars-gold text-black text-[0.6rem] font-bold px-4 py-1.5 disabled:opacity-30 flex items-center gap-1 hover:bg-white transition-all"
                    >
                      <ListTodo className="w-3 h-3" /> TO CHANGE LOG
                    </button>
                    <button 
                      onClick={() => handleStatusChange(ticket.id!, 'addressed')}
                      disabled={ticket.status === 'addressed'}
                      className="lcars-pill bg-lcars-green text-black text-[0.6rem] font-bold px-4 py-1.5 disabled:opacity-30 flex items-center gap-1 hover:bg-white transition-all"
                    >
                      <CheckCircle2 className="w-3 h-3" /> MARK ADDRESSED
                    </button>
                    <button 
                      onClick={() => handleStatusChange(ticket.id!, 'cleared')}
                      disabled={ticket.status === 'cleared'}
                      className="lcars-pill bg-white/20 text-[var(--foreground)] text-[0.6rem] font-bold px-4 py-1.5 disabled:opacity-30 flex items-center gap-1 hover:bg-lcars-red transition-all"
                    >
                      <XCircle className="w-3 h-3" /> CLEAR
                    </button>
                  </div>
                </div>

                {/* Right Col: Screenshot */}
                <div className="w-full lg:w-64 shrink-0 border-t lg:border-t-0 lg:border-l border-white/10 pt-4 lg:pt-0 lg:pl-6 flex flex-col">
                  <span className="text-[0.6rem] text-lcars-red font-bold uppercase tracking-widest block mb-2">Visual Scan</span>
                  {ticket.screenshotBase64 ? (
                    <button 
                      type="button"
                      onClick={() => setSelectedImage(ticket.screenshotBase64)}
                      className="group relative cursor-pointer rounded-lg overflow-hidden border-2 border-white/10 hover:border-lcars-red transition-all block w-full p-0 text-left"
                      aria-label="View Anomaly Scan Fullscreen"
                    >
                      <img src={ticket.screenshotBase64} alt="Anomaly Scan" className="w-full h-auto object-cover opacity-80 lg:group-hover:opacity-100 transition-opacity" />
                      <div className="absolute inset-0 bg-[var(--background)]/50 flex items-center justify-center lg:opacity-0 lg:group-hover:opacity-100 transition-opacity backdrop-blur-[2px]">
                        <ImageIcon className="w-8 h-8 text-lcars-red drop-shadow-md" />
                      </div>
                    </button>
                  ) : (
                    <div className="bg-[var(--background)]/50 border border-white/5 rounded-lg flex-1 flex flex-col items-center justify-center text-[var(--foreground)]/20 min-h-[100px] lg:min-h-[150px]">
                      <ImageIcon className="w-8 h-8 mb-2 opacity-20" />
                      <span className="text-[0.6rem] font-bold uppercase tracking-widest">No Scan Data</span>
                    </div>
                  )}
                </div>

              </div>
            </div>
          ))
        )}
      </div>

      {/* Fullscreen Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[150] bg-[var(--background)]/90 flex items-center justify-center p-4 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setSelectedImage(null)}
          onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') setSelectedImage(null); }}
          role="button"
          tabIndex={0}
          aria-label="Close fullscreen image"
        >
          <div className="relative max-w-7xl max-h-[100dvh]">
             <div className="absolute top-4 right-4 bg-[var(--background)]/50 px-3 py-1 rounded-full text-[0.6rem] text-lcars-red font-bold tracking-widest uppercase border border-lcars-red/30 backdrop-blur-md">
               Click anywhere to close
             </div>
             <img src={selectedImage} alt="Expanded Anomaly Scan" className="max-w-full max-h-[90vh] object-contain border border-white/20 rounded-lg shadow-2xl" />
          </div>
        </div>
      )}

    </div>
  );
}
